import { c, msgid, ngettext } from 'ttag';

import { formatList } from '@freelancelabs/shared';

import { ContactOnboardingStatus, DataScope } from '../../../graphql/__generated__/schema';
import { EMPTY } from '../display';
import { contactStatusReading } from './contactLookup';
import { OnboardingRole, findRole, isRefused } from './grantableRoles';

/**
 * What `accountActivities` pre-ticked on one account, kept because a proposal and a
 * decision are not the same thing and the screen has to keep telling them apart.
 *
 * It carries the **address it was computed for**, which is what makes the
 * pre-ticking an *initialisation* rather than a constraint: it is applied once per
 * (account × address), so unticking sticks, and correcting the address at step 1
 * after coming back from step 2 redoes it for the person actually being onboarded
 * instead of leaving someone else's markets ticked.
 */
export interface ActivitySuggestion {
    /** The address it was computed for. A different one means it must be redone. */
    email: string;
    /**
     * PCG-305. Exactement ce que le pré-cochage a **AJOUTÉ**, par type, et jamais ce que la
     * source désigne.
     *
     * C'est ce qui rend le changement d'adresse sûr, et c'est la règle que PCG-139 avait déjà
     * payée un cran plus haut : `applyPatch` retire exactement ces lignes et laisse tout ce
     * qui a été coché à la main. Ranger les activités **désignées** retirerait une activité
     * cochée à la main que la source nomme aussi, et garderait une activité posée par la
     * requête.
     *
     * Attention, ON NE STOCKE PLUS DE COMPTEUR. La version par marché rangeait
     * `marketId → nombre d'affaires`, parce qu'une case cochée sans raison à côté est un
     * périmètre accordé par personne. Ici la raison est **sur la ligne elle-même** : c'est le
     * badge « désigné par la source », que l'API sert sur chaque activité. Le compteur serait
     * une seconde source pour le même fait, libre de contredire la ligne.
     */
    needIds: string[];
    serviceIds: string[];
    /**
     * How many ingested contacts the address matched. Above one it is a shared
     * mailbox, and the suggestion then mixes two people's business: the screen says
     * so rather than letting it pass as one person's perimeter.
     */
    matchedContactCount: number;
}

/**
 * What one account will carry for this person: a role, and how far they see on it.
 *
 * It is a **list**, not a single role plus a single perimeter for the whole
 * onboarding: role, status and perimeter belong to the couple (person × account)
 * and never to the person. `InviteAttachmentInput` says the same thing on the API
 * side, which is why one screen sends them all at once.
 */
export interface DraftAttachment {
    organizationId: string;
    /** Null until picked: a role posted by default would be a role nobody chose. */
    roleId: string | null;
    /**
     * Attention, LE NOM NE DIT PLUS CE QU'IL FAIT. Il vaut toujours ALL ou RESTRICTED, mais
     * une restriction se pose désormais sur des **activités** et non sur des marchés. Le
     * renommage est un lot à lui, côté API comme ici.
     */
    dataScope: DataScope;
    /**
     * Les activités cochées. Vides quand la portée est ALL.
     *
     * **L'UNE OU L'AUTRE suffit** quand elle est RESTRICTED : un périmètre peut ne porter que
     * des besoins, et les exiger toutes les deux refuserait quelqu'un qui ne suit que ceux-là.
     */
    needIds: string[];
    serviceIds: string[];
    /**
     * What was proposed, and for whom. Null until the suggestion has been applied,
     * which is also the state it returns to when the perimeter leaves RESTRICTED, so
     * that coming back offers the pre-ticking again instead of an empty list the
     * operator would read as « cette personne n'a aucune affaire ».
     */
    suggestion: ActivitySuggestion | null;
}

/**
 * The selection this account starts from, once the suggestion for `email` is known.
 *
 * The union is not a detail. Dropping the **previous** suggestion before adding the
 * new one is what a corrected address requires: markets ticked from the old
 * address's business must not survive into the new person's perimeter. Everything
 * the operator ticked by hand does survive: it is a decision, and no query gets to
 * undo it.
 */
const unionWithoutStale = (current: string[], previouslySuggested: string[], suggested: string[]): string[] => {
    const stale = new Set(previouslySuggested);
    const decidedByHand = current.filter((id) => !stale.has(id));
    return [...new Set([...decidedByHand, ...suggested])];
};

export const initialActivityIds = (
    attachment: DraftAttachment,
    suggestedNeeds: string[],
    suggestedServices: string[]
): { needIds: string[]; serviceIds: string[] } => ({
    needIds: unionWithoutStale(attachment.needIds, attachment.suggestion?.needIds ?? [], suggestedNeeds),
    serviceIds: unionWithoutStale(attachment.serviceIds, attachment.suggestion?.serviceIds ?? [], suggestedServices),
});

/**
 * What the **contact's own accounts** pre-ticked at step 2, and for which address
 * (PCG-139).
 *
 * Same device as `ActivitySuggestion`, one level up: there it is a perimeter inside an
 * account, here it is the accounts themselves. It has to live on the draft rather
 * than on an attachment for the plain reason that an **unticked** suggestion has no
 * attachment left to hang from, and unticking has to stick.
 *
 * Attention, its list is what the pre-ticking **added**, and nothing else. That is what makes
 * a change of address safe: `applyPatch` drops exactly these attachments and leaves
 * every account the operator ticked by hand, which is a decision no query gets to
 * undo. Storing the accounts the source *designates* instead would drop a hand-ticked
 * account the source happened to name, and keep one the query had ticked.
 */
export interface AccountSuggestion {
    /** The address it was resolved for. A different one means it must be redone. */
    email: string;
    /** Exactly the accounts the pre-ticking added, in the order it added them. */
    accountIds: string[];
}

/**
 * One dashboard about to be granted.
 *
 * The account travels with the id, and it is not redundant. A dashboard whose
 * account has left the selection is refused by `inviteUser` with
 * `FORBIDDEN_OUT_OF_SCOPE` at the very last step, so unticking an account at step
 * 2 has to drop the dashboards that hung from it, and step 2 can only do that if
 * the draft says which account each one belongs to. Storing bare ids would push
 * that clean-up into a re-query, which is a round trip to learn something we
 * already knew.
 */
export interface DraftDashboard {
    id: string;
    organizationId: string;
}

/**
 * The contact picked at step 1: **the identity, as the source writes it**.
 *
 * Step 1 no longer types a first name, a last name or a function: it resolves an
 * address into ingested contacts and the operator picks one. `InviteUserInput` still
 * carries the three fields, so they are filled from here rather than from a keystroke.
 *
 * Attention, it carries the **address it was resolved for**, and that is not bookkeeping. It
 * is the same device as `ActivitySuggestion.email`, for the same accident: correcting
 * the address after the fact must not leave the former person's identity attached to
 * the new one. `applyPatch` drops it on that basis, so everything downstream can read
 * `draft.contact` without re-checking which address it belongs to: it is either the
 * current one, or null.
 */
export interface DraftContact {
    /**
     * Our contact uuid: **sent with the invitation** since PCG-85, where `contactId`
     * became `ID!` on `InviteUserInput`.
     *
     * It was held here and sent nowhere for the whole of PCG-84, which is what made the
     * strict rule an appearance: the screen refused an address the source did not know,
     * the mutation never read the contact table and accepted it. The API now confronts
     * this id with the address and with the actor's portfolio, and writes the
     * `contact_onboarding` link, so the id is also what makes a later search of the
     * same address answer « déjà onboardée ».
     */
    id: string;
    /** The address it was resolved for. A different one means it must be picked again. */
    email: string;
    firstName: string | null;
    lastName: string | null;
    /** Verbatim from the source: real job titles, tidying them would invent others. */
    jobFunction: string | null;
    /** The business reference the source carries, « SF-00412 ». Shown, never sent. */
    businessReference: string | null;
    /**
     * The scale the API arbitrates, kept so the recap can re-read it at the send.
     * Never re-derived from the accounts: two derivations of one fact drift.
     */
    onboardingStatus: ContactOnboardingStatus;
}

export interface Draft {
    email: string;
    /** Null until a contact is picked. Step 1 blocks until it is not. */
    contact: DraftContact | null;
    attachments: DraftAttachment[];
    /** What the contact's accounts pre-ticked, and for which address. Null until applied. */
    accountSuggestion: AccountSuggestion | null;
    /** Optional step: an empty list is « rien attribué », not « pas encore décidé ». */
    dashboards: DraftDashboard[];
    /** Optional step. One list at most: `InviteUserInput` carries a single id. */
    partnerListId: string | null;
}

/**
 * Un brouillon vide, ou pré-rempli d'une adresse quand on ARRIVE depuis « Ajouter un
 * compte » d'une fiche existante (PCG-357). Seule l'adresse est semée : le contact reste
 * à choisir par la recherche de l'étape 1, la vérification d'identité étant voulue (voir
 * `IdentityStep`). L'assistant traite ensuite le contact « déjà onboardé » comme une
 * bifurcation qui ajoute des rattachements, sans second utilisateur.
 */
export const emptyDraft = (email = ''): Draft => ({
    email,
    contact: null,
    attachments: [],
    accountSuggestion: null,
    dashboards: [],
    partnerListId: null,
});

export const STEP_COUNT = 5;

/**
 * One attachment after the address has changed: whatever the **previous** address's
 * suggestion had ticked is dropped, and the snapshot with it.
 *
 * Everything the operator ticked by hand stays. Only what a query put there for
 * someone else goes.
 */
const withoutStaleSuggestion = (attachment: DraftAttachment): DraftAttachment => {
    if (!attachment.suggestion) {
        return attachment;
    }
    const staleNeeds = new Set(attachment.suggestion.needIds);
    const staleServices = new Set(attachment.suggestion.serviceIds);
    return {
        ...attachment,
        needIds: attachment.needIds.filter((id) => !staleNeeds.has(id)),
        serviceIds: attachment.serviceIds.filter((id) => !staleServices.has(id)),
        suggestion: null,
    };
};

/**
 * The single place the draft changes, because one rule has to hold whichever screen
 * is on: **nothing resolved from one address may survive into another**.
 *
 * Three things are resolved from the address, and all of them go stale the same way:
 *
 * - the **markets pre-ticked** for that person's business (PCG-63);
 * - the **identity**: first name, last name and function no longer typed but read
 *   off the contact picked at step 1 (PCG-84);
 * - the **accounts pre-ticked** from the contact's own accounts at step 2 (PCG-139),
 *   which is the gravest of the three: an attachment is what the invitation grants,
 *   so one surviving a change of address opens somebody an access to the accounts of
 *   the person who was being onboarded a minute earlier.
 *
 * The step 2 row re-initialises itself when the address changes under it, but only
 * while it is mounted, and it is not. Once the review has been reached, « Modifier »
 * goes to step 1 and « Revenir au récapitulatif » comes straight back to step 5, so
 * an address can be corrected and the invitation sent without step 2 ever rendering
 * again. The markets ticked from the former person's business would be granted to
 * the new one, and nothing on screen would have said so.
 *
 * The identity is worse still: it is sent verbatim to `inviteUser`, so a stale one
 * would invite the **new** address under the **old** person's name: and, unlike the
 * markets, nothing later in the journey would even display it.
 *
 * Hence the rule lives on the patch, not in the row: every path goes through here.
 *
 * Attention, it is also what lets the rest of the journey read `attachment.suggestion` and
 * `draft.contact` without re-checking their address: either is the current one, or
 * null. Step 1 then blocks on the null, so a corrected address cannot reach the send
 * without a contact having been picked for it.
 */
export const applyPatch = (draft: Draft, patch: Partial<Draft>): Draft => {
    const next = { ...draft, ...patch };
    if (next.email.trim() === draft.email.trim()) {
        return next;
    }
    // Compared against the **new** address, exactly like the identity below: a
    // suggestion carrying the address being set is the one being resolved, not a
    // leftover. Null when it is stale, and its accounts then leave with it.
    const staleAccounts =
        next.accountSuggestion && next.accountSuggestion.email.trim() !== next.email.trim()
            ? new Set(next.accountSuggestion.accountIds)
            : null;
    return {
        ...next,
        // Compared against the **new** address, not merely dropped: picking a contact
        // and setting the address in one patch is how step 1 records its choice, and
        // that identity is not stale: it is the one being resolved.
        contact: next.contact && next.contact.email.trim() === next.email.trim() ? next.contact : null,
        accountSuggestion: staleAccounts ? null : next.accountSuggestion,
        attachments: next.attachments
            .filter((attachment) => !staleAccounts?.has(attachment.organizationId))
            .map(withoutStaleSuggestion),
        // The same clean-up `removeAttachment` does, and for the same refusal: a
        // dashboard whose account has left the selection is refused at the send with
        // `FORBIDDEN_OUT_OF_SCOPE`, on the one screen where nothing can be fixed.
        dashboards: next.dashboards.filter((dashboard) => !staleAccounts?.has(dashboard.organizationId)),
    };
};

/**
 * Deliberately loose. The address is verified by the invitation actually arriving,
 * not by a regular expression; the only thing worth refusing here is what plainly
 * cannot be an address, so that the operator is not stopped by a valid but unusual
 * one.
 */
const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * The roles picked in the draft that the operator may not grant, or null when there
 * are none.
 *
 * A role is picked from a list where the refused ones are already disabled, so this
 * catches what happens **after** the pick: the answer to `roles` is judged on the
 * actor's own membership, and that membership can change under a journey that is
 * already half-composed. The selection then stops being valid without anything on
 * screen having moved.
 *
 * Letting it through would push the refusal to the very last click, which is the
 * defect the whole lot exists to remove: the API stopped filtering its list precisely
 * so the screen could say no earlier.
 *
 * Attention, silent while `roles` is loading or has failed: `isRefused` only refuses on a role
 * it holds. A blocker built on an absent list would stop the journey on ignorance.
 */
const notGrantableBlocker = (draft: Draft, roles: OnboardingRole[]): string | null => {
    const names = [
        ...new Set(
            draft.attachments
                .filter((attachment) => isRefused(roles, attachment.roleId))
                .map((attachment) => findRole(roles, attachment.roleId)?.name ?? EMPTY)
        ),
    ];
    if (names.length === 0) {
        return null;
    }
    const list = formatList(names);
    return ngettext(
        msgid`The role ${list} is no longer one you can grant. Pick another one: the invitation would be refused when sent.`,
        `The roles ${list} are no longer ones you can grant. Pick others: the invitation would be refused when sent.`,
        names.length
    );
};

/**
 * Why the operator cannot move on yet, or null when they can. The footer prints it
 * next to a disabled button: a button that refuses without saying why sends people
 * hunting through the form.
 */
export const stepBlocker = (step: number, draft: Draft, roles: OnboardingRole[]): string | null => {
    if (step === 1) {
        if (!draft.email.trim()) {
            return c('Onboarding blocker').t`Enter the email address of the person to onboard.`;
        }
        if (!looksLikeEmail(draft.email)) {
            return c('Onboarding blocker').t`This email address is not valid.`;
        }
        // The strict option, on screen: only a known contact is onboarded, so the
        // journey does not move on an address that resolved to nobody, or to
        // somebody the operator has not picked between two.
        //
        // Attention, `applyPatch` has already dropped an identity resolved for another
        // address, so this also catches the address corrected after the fact: the
        // contact goes null and step 1 refuses again, rather than the invitation
        // leaving under the former person's name.
        if (!draft.contact) {
            return c('Onboarding blocker')
                .t`Search the address and pick the contact to onboard: the identity comes from the source, not from this screen.`;
        }
        if (!contactStatusReading(draft.contact.onboardingStatus).selectable) {
            // Reachable when a contact was picked and the answer changed under the
            // draft: a re-search, or an ingestion run between the pick and the send.
            return c('Onboarding blocker')
                .t`The contact picked can no longer be onboarded. Search the address again and read the reason given next to them.`;
        }
        return null;
    }

    if (step === 2) {
        if (draft.attachments.length === 0) {
            return c('Onboarding blocker').t`Add at least one account.`;
        }
        if (draft.attachments.some((attachment) => !attachment.roleId)) {
            return c('Onboarding blocker').t`Pick a role on every account.`;
        }
        // The API refuses it too. Saying it here spares a round trip, and names the
        // account rather than letting a server error name none.
        if (
            draft.attachments.some(
                (attachment) =>
                    attachment.dataScope === DataScope.Restricted &&
                    attachment.needIds.length === 0 &&
                    attachment.serviceIds.length === 0
            )
        ) {
            return c('Onboarding blocker')
                .t`A restricted perimeter needs at least one activity, otherwise the person sees nothing.`;
        }
        return notGrantableBlocker(draft, roles);
    }

    if (step === STEP_COUNT) {
        // **Attention, one block, and it re-runs the earlier steps.** There used to be two
        // `if (step === STEP_COUNT)` here: the first returned `notGrantableBlocker`
        // unconditionally, so the second (the one re-running step 2's checks) was
        // unreachable, and the review only ever caught a stale role. That is the very
        // defect the comment below describes, sitting dead under the comment.
        //
        // The last screen re-runs the earlier checks rather than trusting that they
        // held when those steps were left. They can stop holding **after**:
        // correcting the address at step 1 drops the identity and what the previous
        // suggestion had ticked, and « Revenir au récapitulatif » reaches here
        // without step 1 or step 2 rendering again, so an empty restricted
        // perimeter, or an address with no contact picked for it, can arrive at the
        // one screen that sends.
        //
        // Attention, the API refuses **both** since PCG-85: a missing contact can no longer
        // even be expressed, `contactId` being `ID!`. The check stays here all the
        // same, and is not made redundant by that: it refuses **before** the send,
        // naming the step to go back to, where the API can only refuse after, with a
        // code the screen then has to translate. The server holding the rule is what
        // makes it a rule; the screen holding it is what makes it usable.
        //
        // Roles travel too: one can stop being grantable while the operator sits on
        // this screen, and step 2 is not mounted to notice it. `stepBlocker(2, …)`
        // ends on `notGrantableBlocker`, so that check is carried here by the call.
        return stepBlocker(1, draft, roles) ?? stepBlocker(2, draft, roles);
    }

    // Steps 3 and 4 never block, and that is the decision of the lot rather than an
    // oversight: both attributions are optional, and with both catalogues empty
    // everywhere today, attributing nothing is what will happen to everybody. A
    // journey that asked for a dashboard would be a journey nobody could finish.
    return null;
};
